import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import { eventEnded } from '../lib/format';
import type { PublicPasses as Data } from '../lib/types';
import { groupByDay, PassRow } from '../components/resident';
import { FlatPassCard, FlatPassModal, MealPassModal, PdfButton, type PassScope } from '../components/passes';
import { LOGO_SRC, Spinner } from '../components/ui';
import { LiveStrip } from '../components/live';

const cacheKey = (token: string) => `one10:pub:${token}`;

/**
 * The "your meal passes" link from emails and WhatsApp. No sign-in: the token in the link is the secret.
 * Keeps the last copy on the phone so the QR still shows without signal at the venue.
 */
export default function PublicPasses() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [flatOpen, setFlatOpen] = useState(false);
  const q = useQuery({
    queryKey: ['public-passes', token],
    queryFn: async () => {
      const d = await api.publicPasses(token);
      try {
        if (d) localStorage.setItem(cacheKey(token), JSON.stringify(d));
        else localStorage.removeItem(cacheKey(token));
      } catch { /* storage unavailable */ }
      return d;
    },
    placeholderData: () => {
      try {
        const raw = localStorage.getItem(cacheKey(token));
        return raw ? (JSON.parse(raw) as Data) : undefined;
      } catch {
        return undefined;
      }
    },
    refetchInterval: 30_000,
  });

  const data = q.data;
  // Live events only: last year's passes stay with the family's sign-in, under Past events.
  const passes = (data?.passes ?? []).filter((p) => p.state !== 'void' && !eventEnded(p.sitting.event_status));
  const eventName = [...new Set(passes.map((p) => p.sitting.event_name).filter(Boolean))].join(' · ');
  const days = groupByDay(passes);
  const current = openId ? passes.find((p) => p.id === openId) ?? null : null;
  const scope: PassScope | null = data?.flat.pass_token ? {
    flat: { label: data.flat.label, name: data.flat.name, passToken: data.flat.pass_token },
    token,
    reissueHint: 'ask the Food Committee to issue new QR codes',
    onChanged: () => void qc.invalidateQueries({ queryKey: ['public-passes', token] }),
  } : null;
  const confirmed = passes.some((p) => p.booked > 0);

  return (
    <div className="shell">
      <div className="app-bar">
        <div className="pub-head">
          <span className="brand-chip" style={{ justifySelf: 'start' }}><img src={LOGO_SRC} alt="ONE10" style={{ height: 32 }} /></span>
          <p className="app-eyebrow">{eventName ? `${eventName} · ` : ''}Meal passes</p>
          {data && <p className="pub-flat">Flat {data.flat.label}</p>}
          {data && <p className="app-sub">{data.flat.name}</p>}
        </div>
      </div>
      <main className="app-body">
        {q.isPending && !data ? <div className="empty"><Spinner /></div>
          : q.isError && !data ? <p className="err">{errorText(q.error)}</p>
          : data === null ? (
            <div className="card empty">
              <p className="display" style={{ fontSize: 20 }}>This link no longer works</p>
              <p>The Food Committee may have issued new passes for this flat. Ask them, or sign in to see your passes.</p>
              <Link className="btn primary" to="/signin">Sign in</Link>
            </div>
          ) : (
            <>
              {q.isError && <p className="fine">Showing the last saved passes. You seem to be offline.</p>}
              <LiveStrip />
              {scope && confirmed && <FlatPassCard scope={scope} passes={passes} onOpen={() => setFlatOpen(true)} />}
              {days.length === 0 && <div className="card empty"><p>No confirmed meals on this flat yet.</p></div>}
              {days.map((d) => (
                <section className="pday" key={d.key}>
                  <p className="pday-h"><span className="bn">{d.bn}</span>{d.label}<span className="pday-date">{d.date}</span></p>
                  {d.passes.map((p) => <PassRow key={p.id} pass={p} onOpen={() => setOpenId(p.id)} />)}
                </section>
              ))}
              {scope && confirmed && <PdfButton scope={scope} passes={passes} block />}
              <p className="fine">
                The family pass works at every meal. Tap a meal for its own QR, to share it, or to give a guest a single seat. The counter can
                serve some seats now and the rest later.
              </p>
              <p className="fine">To book more meals or pay, <Link to="/signin">sign in</Link>.</p>
            </>
          )}
      </main>
      {current && scope && <MealPassModal pass={current} scope={scope} onClose={() => setOpenId(null)} />}
      {flatOpen && scope && <FlatPassModal scope={scope} passes={passes} onClose={() => setFlatOpen(false)} />}
    </div>
  );
}
